import { partition } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, XStack } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { YStack } from '@/components/ui/layout/layout';
import { FeedRowAction, FeedTrayCta } from '@/features/action-cards-view/feed';
import { Card } from '@/features/integrations/add-integrations-modal';
import type { IntegrationSuggestion } from '@/features/integrations/integrations.types';
import { useTabs } from '@/features/tabs';
import { Widget } from '@/features/widgets/widget.parts';
import { cn } from '@/lib/utils';

import { ConfigurableSuggestionRow } from './ConfigurableSuggestionRow';
import {
  ConnectedBadge,
  SuggestionCard,
  SuggestionIcon,
} from './suggestion-card';
import {
  useIntegrationSuggestions,
  useSuggestionShown,
} from './use-integration-suggestions';

import type { HomeSectionId } from './home-preferences.store';

export { SuggestionIcon };

export const RemoveAccountDialog = ({
  suggestion,
  onConfirm,
  onCancel,
}: {
  suggestion: IntegrationSuggestion | null;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const name = suggestion?.display_name ?? '';
  return (
    <Dialog.Root
      open={suggestion !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <Dialog.Content className="max-w-sm">
        <Dialog.Header>
          <Dialog.Title>Remove {name}?</Dialog.Title>
          <Dialog.Description>
            This removes {name} from Littlebird. You&apos;ll need to reconnect
            it to use it again.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer>
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm}>
            Remove account
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

type SuggestionActions = Pick<
  ReturnType<typeof useIntegrationSuggestions>,
  'connect' | 'dismiss' | 'tryIt' | 'removingAccountId'
> & { onRemove: (suggestion: IntegrationSuggestion) => void };

const SUGGESTION_LIMIT = 6;

const SECTION_ID: HomeSectionId = 'integration-suggestions';

// Compact, like the design system's in-card actions, so Home and Integrations match
const TryActions = ({
  suggestion,
  dismiss,
  tryIt,
}: {
  suggestion: IntegrationSuggestion;
} & Pick<SuggestionActions, 'dismiss' | 'tryIt'>) => {
  return (
    <XStack className="shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="xs"
        textSize="xs"
        className="text-foreground-muted hover:text-foreground"
        onClick={() => dismiss(suggestion)}
      >
        Dismiss
      </Button>
      <Button size="xs" textSize="xs" onClick={() => tryIt(suggestion)}>
        Try it
      </Button>
    </XStack>
  );
};

const TrySuggestionCard = ({
  suggestion,
  dismiss,
  tryIt,
  position,
}: {
  suggestion: IntegrationSuggestion;
  position: number;
} & Pick<SuggestionActions, 'tryIt' | 'dismiss'>) => {
  const shownRef = useSuggestionShown(suggestion, position, 'home');
  return (
    <SuggestionCard
      suggestion={suggestion}
      shownRef={shownRef}
      dismiss={{
        label: `Dismiss ${suggestion.display_name}`,
        onClick: () => dismiss(suggestion),
      }}
      badge={<ConnectedBadge className="-bottom-1 -right-1 size-4" />}
      title={`${suggestion.display_name} is connected`}
      message={`Try asking: “${suggestion.reason}”`}
      actions={
        <Button
          size="xs"
          textSize="xs"
          className="ac-scale h-auto min-h-8 w-full max-w-full justify-center whitespace-normal break-words px-3 py-1 text-center text-[length:calc(12.5*var(--ac-u))] leading-4"
          onClick={() => tryIt(suggestion)}
        >
          Try it
        </Button>
      }
    />
  );
};

const ConnectSuggestionCard = ({
  suggestion,
  position,
  connect,
  dismiss,
}: {
  suggestion: IntegrationSuggestion;
  position: number;
} & Pick<SuggestionActions, 'connect' | 'dismiss'>) => {
  const shownRef = useSuggestionShown(suggestion, position, 'home');
  return (
    <SuggestionCard
      suggestion={suggestion}
      shownRef={shownRef}
      dismiss={{
        label: `Dismiss ${suggestion.display_name}`,
        onClick: () => dismiss(suggestion),
      }}
      title={
        <>
          {suggestion.display_name}
          {suggestion.account_label && (
            <span className="text-foreground-muted">
              {' · '}
              {suggestion.account_label}
            </span>
          )}
        </>
      }
      message={suggestion.reason}
      actions={
        <FeedTrayCta
          label="Connect"
          accessibleLabel={`Connect ${suggestion.display_name}`}
          className="ac-scale w-full min-w-0 h-auto min-h-8 max-w-full justify-center whitespace-normal break-words px-3 py-1 text-center leading-4"
          onClick={() => void connect(suggestion)}
        />
      }
    />
  );
};

const ReconnectSuggestionRow = ({
  suggestion,
  position,
  connect,
  onRemove,
  removingAccountId,
}: {
  suggestion: IntegrationSuggestion;
  position: number;
} & Pick<SuggestionActions, 'connect' | 'onRemove' | 'removingAccountId'>) => {
  const shownRef = useSuggestionShown(suggestion, position, 'home');
  return (
    <XStack
      ref={shownRef}
      className="group/feed-row flex min-w-0 items-center gap-3 rounded-lg pl-2 py-2 transition-colors duration-[120ms] hover:bg-surface-hover has-[:focus-visible]:bg-surface-hover motion-reduce:transition-none"
    >
      <span className="relative inline-flex size-[calc(24rem/14)] shrink-0 items-center justify-center rounded-md bg-surface-active">
        <SuggestionIcon suggestion={suggestion} className="size-3.5" />
        <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-warning-9 ring-2 ring-background" />
      </span>
      <YStack className="min-w-0 flex-1">
        <XStack className="min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="break-words text-sm text-foreground">
            {suggestion.display_name}
            {suggestion.account_label && (
              <span className="text-foreground-muted">
                {' · '}
                {suggestion.account_label}
              </span>
            )}
          </span>
          <span className="text-xs text-foreground-muted">
            Needs reconnecting
          </span>
        </XStack>
        <span className="break-words text-xs text-foreground-muted">
          {suggestion.reason}
        </span>
      </YStack>
      <XStack className="ac-scale ml-auto shrink-0 items-center gap-[calc(2*var(--ac-u))]">
        <FeedRowAction
          label="Remove"
          icon={Icons.trash2}
          accessibleLabel={`Remove ${suggestion.display_name}`}
          disabled={removingAccountId === suggestion.account_id}
          onClick={() => onRemove(suggestion)}
        />
        <FeedTrayCta
          label="Reconnect"
          accessibleLabel={`Reconnect ${suggestion.display_name}`}
          className="ml-[calc(4*var(--ac-u))] h-auto min-h-8 max-w-full whitespace-normal break-words px-3 py-1 text-center leading-4"
          onClick={() => void connect(suggestion)}
        />
      </XStack>
    </XStack>
  );
};

const JustConnectedSuggestionCard = ({
  suggestion,
  position,
  dismiss,
  tryIt,
}: {
  suggestion: IntegrationSuggestion;
  position: number;
} & Pick<SuggestionActions, 'dismiss' | 'tryIt'>) => {
  const shownRef = useSuggestionShown(suggestion, position, 'integrations');
  return (
    <div
      ref={shownRef}
      className="rounded-2xl border border-success-6 bg-success-3"
    >
      <Card
        icon={<SuggestionIcon suggestion={suggestion} className="size-full" />}
        badge={<ConnectedBadge className="-bottom-1 -right-1 size-4" />}
        name={`${suggestion.display_name} is connected`}
        description={`Try asking: “${suggestion.reason}”`}
        action={
          <TryActions suggestion={suggestion} dismiss={dismiss} tryIt={tryIt} />
        }
      />
    </div>
  );
};

// The same try entries Home shows, at the top of Integrations, where the user lands after connecting
export const JustConnectedSuggestions = observer(() => {
  const { enabled, suggestions, dismiss, tryIt } =
    useIntegrationSuggestions('integrations');
  const tries = suggestions.filter((s) => s.action === 'try');

  if (!enabled || tries.length === 0) return null;
  return (
    <YStack className="gap-4" role="region" aria-label="Just connected">
      <h3 className="text-sm font-medium text-foreground-muted">
        Just connected
      </h3>
      <YStack className="gap-5">
        {tries.map((suggestion, index) => (
          <JustConnectedSuggestionCard
            key={suggestion.toolkit_slug}
            suggestion={suggestion}
            position={index + 1}
            dismiss={dismiss}
            tryIt={tryIt}
          />
        ))}
      </YStack>
    </YStack>
  );
});

export const IntegrationSuggestionsSection = observer(() => {
  const {
    enabled,
    suggestions,
    connect,
    dismiss,
    tryIt,
    remove,
    removingAccountId,
  } = useIntegrationSuggestions('home');
  const { openTab } = useTabs();
  const [removing, setRemoving] = useState<IntegrationSuggestion | null>(null);
  const [showsAll, setShowsAll] = useState(false);

  if (!enabled || suggestions.length === 0) return null;

  const [reconnects, connects] = partition(
    suggestions,
    (suggestion) => suggestion.action === 'reconnect'
  );
  const ordered = [reconnects, connects].flatMap((group) =>
    group.map((suggestion, index) => ({ suggestion, position: index + 1 }))
  );
  const shown = showsAll ? ordered : ordered.slice(0, SUGGESTION_LIMIT);
  const shownReconnects = shown.filter(
    ({ suggestion }) => suggestion.action === 'reconnect'
  );
  const shownCards = shown.filter(
    ({ suggestion }) => suggestion.action !== 'reconnect'
  );
  const hiddenCount = ordered.length - shown.length;

  return (
    <Widget id={SECTION_ID}>
      <Widget.Header
        title="Connections"
        count={suggestions.length}
        accessory={
          <Widget.HeaderButton
            onClick={() =>
              openTab({ path: '/integrations' }, { reuseExisting: true })
            }
          >
            Manage
          </Widget.HeaderButton>
        }
      />
      <Widget.Body>
        <div className="[container-type:inline-size]">
          {shownCards.length > 0 ? (
            <div
              className="grid grid-cols-1 gap-4 [@container(min-width:480px)]:grid-cols-3"
              role="list"
              aria-label="Suggested connections"
            >
              {shownCards.map(({ suggestion, position }) =>
                suggestion.chip ? (
                  <ConfigurableSuggestionRow
                    key={`${suggestion.account_id ?? suggestion.toolkit_slug}:${suggestion.action}`}
                    suggestion={{ ...suggestion, chip: suggestion.chip }}
                    position={position}
                    connect={connect}
                    dismiss={dismiss}
                  />
                ) : suggestion.action === 'try' ? (
                  <TrySuggestionCard
                    key={suggestion.account_id ?? suggestion.toolkit_slug}
                    suggestion={suggestion}
                    position={position}
                    dismiss={dismiss}
                    tryIt={tryIt}
                  />
                ) : (
                  <ConnectSuggestionCard
                    key={suggestion.account_id ?? suggestion.toolkit_slug}
                    suggestion={suggestion}
                    position={position}
                    connect={connect}
                    dismiss={dismiss}
                  />
                )
              )}
            </div>
          ) : null}
          {shownReconnects.length > 0 ? (
            <YStack className={cn('gap-1', shownCards.length > 0 && 'mt-6')}>
              {shownReconnects.map(({ suggestion, position }) => (
                <ReconnectSuggestionRow
                  key={suggestion.account_id ?? suggestion.toolkit_slug}
                  suggestion={suggestion}
                  position={position}
                  connect={connect}
                  onRemove={setRemoving}
                  removingAccountId={removingAccountId}
                />
              ))}
            </YStack>
          ) : null}
        </div>
        {hiddenCount > 0 || showsAll ? (
          <button
            type="button"
            onClick={() => setShowsAll((all) => !all)}
            className="self-start rounded-md px-2 py-1 text-xs text-foreground-muted outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-accent"
          >
            {showsAll ? 'Show less' : `Show ${hiddenCount} more`}
          </button>
        ) : null}
      </Widget.Body>
      <RemoveAccountDialog
        suggestion={removing}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) void remove(removing);
          setRemoving(null);
        }}
      />
    </Widget>
  );
});
